import { Selection } from '../selection';
import { timeSource } from '../time/global-time-source';
import FlightItem from './flight-item';
import { FlightUpdateGeojsonProps, IFlightItem } from './types';

export class FlightsUpdater {
    private readonly selection: Selection;
    private readonly workspaceId: number;
    private readonly flightMap = new Map<number, IFlightItem>();
    private _data: IFlightItem[] = [];
    private _version: number = 0;
    private _coastOffset = 0;

    constructor(selection: Selection, workspaceId: number) {
        this.selection = selection;
        this.workspaceId = workspaceId;
    }

    get version() {
        return this._version;
    }

    get data(): IFlightItem[] {
        return this._data;
    }

    get coastOffset(): number {
        return this._coastOffset;
    }

    getFlight(recordId: number) {
        return this.flightMap.get(recordId);
    }

    hasFlight(recordId: number) {
        return this.flightMap.has(recordId);
    }

    getSelectedFlights() {
        return this.selection
            .getSelected()
            .map((x) => this.flightMap.get(x))
            .filter((x) => !!x);
    }

    update(data: { updates: FlightUpdateGeojsonProps[]; deleted?: number[]; replace?: true }) {
        performance.mark('Flight updater start');

        this._coastOffset = timeSource.currentTimeMs / 1000;
        if (data.replace) {
            this.flightMap.clear();
            this._data = data.updates
                .filter((x) => !!x.filterState[this.workspaceId])
                .map((update) => {
                    const flight = new FlightItem(update.id);
                    flight.update(update, this.workspaceId, this._coastOffset);
                    return flight;
                });
            this._data.forEach((flight) => this.flightMap.set(flight.id, flight));
        } else {
            if (data.deleted && data.deleted.length > 0) {
                this._data = this._data.filter((x) => !data.deleted!.includes(x.id));
                data.deleted.forEach((x) => this.flightMap.delete(x));
            }

            for (const update of data.updates) {
                if (!update.filterState[this.workspaceId]) {
                    continue;
                }

                if (!this.flightMap.has(update.id)) {
                    this.flightMap.set(update.id, new FlightItem(update.id));
                    this._data.push(this.flightMap.get(update.id)!);
                }

                this.flightMap.get(update.id)!.update(update, this.workspaceId, this._coastOffset);
            }
            // NOTE: If we supply the same data instance to deck it won't see it as an update
            // Update triggers are one solution, but they are meant to be used only when the data doesn't not change just when the attribute accessor function changes
            // For that reason we should be putting 'all' trigger in updateTriggers objects that is tied to flight list version .e.g { all: [this.version] }
            // Another solution is to spread the array to generate a new instance of the array. Not sure how expensive it is but if we can avoid it I guess we should.
        }
        this._version++;
        performance.mark('Flight updater end');
        performance.measure('Flight updater update', 'Flight updater start', 'Flight updater end');
    }
}
